"use client";

import React, { useState } from "react";
import Script from "next/script";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import useWistiaThumbnail from "@/hooks/media/use-wistia-thumbnail";
import useWistiaPlayer from "@/hooks/media/use-wistia-player";
import WISTIA_CONFIG from "@/config/wistia.config";
import {alpha} from "@mui/material";
import { WistiaPlayButton } from "./wistia-play-button";
import styles from "./styles.module.css";

interface WistiaVideoProps {
  videoId: string;
  title?: string;
  startMuted?: boolean;
  deferUntilInteraction?: boolean;
  thumbnailUrl?: string;
  showAuthenticPlayButton?: boolean;
  customPlayButton?: React.ReactNode;
  className?: string;
  testId?: string;
  sx?: any;
  fillHeight?: boolean;
}

const WistiaVideo: React.FC<WistiaVideoProps> = ({
  videoId,
  title,
  startMuted = false,
  deferUntilInteraction = false,
  thumbnailUrl,
  showAuthenticPlayButton = true,
  customPlayButton,
  className,
  testId,
  sx,
  fillHeight = false
}) => {
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  const [shouldLoadPlayer, setShouldLoadPlayer] = useState(!deferUntilInteraction);

  const { thumbnailUrl: fetchedThumbnailUrl, aspectRatio: fetchedAspectRatio } = useWistiaThumbnail(
    videoId,
    thumbnailUrl,
    deferUntilInteraction && !thumbnailUrl
  );

  useWistiaPlayer({
    videoId,
    shouldLoadPlayer,
    deferUntilInteraction,
    startMuted,
    onAspectRatioChange: setAspectRatio
  });

  if (!videoId) {
    return null;
  }

  const handlePlayClick = () => {
    setShouldLoadPlayer(true);
  };

  const displayThumbnailUrl = thumbnailUrl || fetchedThumbnailUrl;
  const displayAspectRatio =
    aspectRatio || fetchedAspectRatio || WISTIA_CONFIG.DEFAULT_ASPECT_RATIO;
  const paddingTop = `${(1 / displayAspectRatio) * 100}%`;
  const testIdPrefix = testId ? `${testId}-` : "";

  if (deferUntilInteraction && !shouldLoadPlayer) {
    return (
      <Box
        className={className || styles.wistiaContainer}
        data-testid={`${testIdPrefix}wistia-video-placeholder`}
        sx={{
          position: "relative",
          paddingBottom: fillHeight
            ? { xs: `${(1 / displayAspectRatio) * 100}%`, desktop: 0 }
            : `${(1 / displayAspectRatio) * 100}%`,
          height: fillHeight ? { xs: 0, desktop: "100%" } : 0,
          overflow: "hidden",
          width: "100%",
          cursor: "pointer",
          ...sx
        }}
        onClick={handlePlayClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handlePlayClick();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={title || `Play video ${videoId}`}
      >
        {displayThumbnailUrl ? (
          <Box
            component="img"
            src={displayThumbnailUrl}
            alt="Video thumbnail"
            data-testid={`${testIdPrefix}wistia-thumbnail`}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover"
            }}
          />
        ) : (
          <Skeleton
            variant="rectangular"
            animation="wave"
            data-testid={`${testIdPrefix}wistia-skeleton-loader`}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              bgcolor: "background.default",
              "&::before": {
                background: (theme) => `linear-gradient(90deg, transparent, ${alpha(theme.palette.common.black, 0.05)}, transparent)`
              }
            }}
          />
        )}

        {displayThumbnailUrl && showAuthenticPlayButton && (
          customPlayButton || (
            <WistiaPlayButton
              ariaLabel={title || `Play video ${videoId}`}
              data-testid={`${testIdPrefix}wistia-play-button`}
            />
          )
        )}
      </Box>
    );
  }

  return (
    <>
      <Box
        className={className || styles.wistiaContainer}
        sx={{ height: fillHeight ? { xs: "auto", desktop: "100%" } : undefined, ...sx }}
        data-testid={`${testIdPrefix}wistia-video-container`}
        aria-label={title || `Wistia video ${videoId}`}
      >
        <Box
          className={styles.wistiaResponsivePadding}
          sx={{
            paddingTop: fillHeight ? { xs: paddingTop, desktop: 0 } : paddingTop,
            height: fillHeight ? { xs: "auto", desktop: "100%" } : undefined
          }}
        >
          <Box className={styles.wistiaResponsiveWrapper}>
            <Box
              data-testid={`${testIdPrefix}wistia-video`}
              className={`wistia_embed wistia_async_${videoId} videoFoam=true ${
                startMuted ? "" : "muted=false"
              } volumeControl=true ${styles.wistiaEmbed}`}
              sx={{
                position: "relative",
                width: "100%",
                height: "100%"
              }}
            >
              &nbsp;
            </Box>
          </Box>
        </Box>
      </Box>
      {shouldLoadPlayer && (
        <Script
          src={WISTIA_CONFIG.EMBED_SCRIPT_URL}
          strategy={deferUntilInteraction ? "afterInteractive" : "lazyOnload"}
          async
        />
      )}
    </>
  );
};

export default WistiaVideo;
